import { StateCreator } from 'zustand';
import { NotificationSlice } from '../../../features/notifications/model/types';
import { translateMessageId } from '../../../shared/i18n';
import { DelegationSlice } from '../../delegation/model/types';
import { normalizeApiError } from '../../../shared/api/lib/error';
import { LoadingState } from '../../../shared/store/model/types';
import { clearStoredToken, getStoredToken } from './session-storage';
import { restoreSession, fetchAuthenticatedProfile } from '../api/session-api';
import { UserSlice } from './types';

type UserStoreState = UserSlice &
  NotificationSlice &
  Pick<DelegationSlice, 'chain' | 'root'> & {
    loading: LoadingState;
  };

export const createUserSlice: StateCreator<
  UserStoreState,
  [],
  [],
  UserSlice
> = (set, get) => ({
  token: getStoredToken(),
  user: null,
  hydrated: false,
  bootstrap: async () => {
    set((state) => ({
      loading: { ...state.loading, bootstrap: true },
    }));

    try {
      const existingToken = get().token ?? getStoredToken();

      if (existingToken) {
        try {
          const restoredSession = await restoreSession(existingToken);

          set((state) => ({
            token: restoredSession.token,
            user: restoredSession.user,
            chain: restoredSession.chain,
            root: restoredSession.root,
            hydrated: true,
            loading: { ...state.loading, bootstrap: false },
          }));
          return;
        } catch {
          clearStoredToken();
        }
      }

      set((state) => ({
        token: null,
        user: null,
        chain: [],
        root: null,
        hydrated: true,
        loading: { ...state.loading, bootstrap: false },
      }));
    } catch (error) {
      const message = normalizeApiError(error, translateMessageId);
      set((state) => ({
        hydrated: true,
        loading: { ...state.loading, bootstrap: false },
      }));
      get().notify(message);
    }
  },
  logout: async () => {
    clearStoredToken();
    set((state) => ({
      token: null,
      user: null,
      chain: [],
      root: null,
      hydrated: false,
      loading: { ...state.loading, bootstrap: false },
    }));

    await get().bootstrap();
  },
  refreshUser: async () => {
    const token = get().token;
    if (!token) return;
    try {
      const user = await fetchAuthenticatedProfile(token);
      set({ user });
    } catch {
      // silently ignore — stale data is acceptable here
    }
  },
});
